import type { ThemeOptions } from '@mui/material/styles';
import { radii, motion } from './tokens/foundation.js';
import { shell, table } from './tokens/layout.js';
import { focusRing } from './tokens/semantic.js';

/**
 * MUI component overrides driven by design-system tokens.
 *
 * These are opinionated defaults for the Finance application. They should not
 * contain arbitrary values; every override resolves from foundation/semantic
 * tokens or the computed theme palette.
 */
export function getComponentOverrides(): ThemeOptions['components'] {
  const focusOutline = (color: string) =>
    `${focusRing.width}px ${focusRing.style} ${color}`;
  const touchTargetMedia = '@media (pointer: coarse), (max-width: 600px)' as const;

  return {
    MuiCssBaseline: {
      styleOverrides: theme => ({
        ':root': {
          colorScheme: theme.palette.mode,
        },
        html: {
          minHeight: '100%',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'clip',
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          minHeight: '100%',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'clip',
          backgroundColor: theme.palette.background.default,
        },
        '#root': {
          minHeight: '100%',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'clip',
        },
        '@keyframes spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '@keyframes pulse-live': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.5, transform: 'scale(0.85)' },
        },
        '@keyframes page-enter': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            scrollBehavior: 'auto !important',
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
          },
        },
        '@media (pointer: coarse), (max-width: 600px)': {
          'select, textarea, input:not([type="hidden"]), [role="combobox"]': {
            minHeight: '44px',
          },
        },
        '@media (prefers-contrast: high)': {
          '*, *::before, *::after': {
            borderWidth: '2px !important',
            outlineWidth: '3px !important',
          },
          '.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px !important',
          },
          '.MuiButton-root': {
            borderWidth: '2px !important',
          },
          '.MuiTableCell-root': {
            borderBottomWidth: '2px !important',
          },
        },
      }),
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          borderRadius: radii.medium,
          fontWeight: 600,
          lineHeight: 1.25,
          minWidth: 88,
          minHeight: 40,
          maxWidth: '100%',
          transition: `background-color ${motion.duration.fast} ${motion.easing.standard}, border-color ${motion.duration.fast} ${motion.easing.standard}, color ${motion.duration.fast} ${motion.easing.standard}, transform 150ms ${motion.easing.standard}`,
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          // MUI's default dark-mode hover darkens a light primary surface but
          // retains its dark contrast text. Keep the accessible light surface
          // for contained controls instead of creating a low-contrast pair.
          ...(theme.palette.mode === 'dark'
            ? {
                '&.MuiButton-contained.MuiButton-colorPrimary:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
                '&.MuiButton-contained.MuiButton-colorSecondary:hover': {
                  backgroundColor: theme.palette.secondary.main,
                },
              }
            : {}),
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-focusVisible': {
            outline: focusOutline(theme.palette.primary.main),
            outlineOffset: focusRing.offset,
          },
          [touchTargetMedia]: {
            minHeight: 44,
          },
        }),
        outlined: { borderRadius: radii.medium },
        sizeSmall: {
          minWidth: 64,
          minHeight: 36,
          [touchTargetMedia]: { minHeight: 44 },
        },
        sizeLarge: { minHeight: 48 },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: false },
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focusVisible': {
            outline: focusOutline(theme.palette.primary.main),
            outlineOffset: focusRing.offset,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 40,
          minHeight: 40,
          borderRadius: radii.medium,
          [touchTargetMedia]: {
            minWidth: 44,
            minHeight: 44,
          },
        },
        sizeSmall: {
          minWidth: 36,
          minHeight: 36,
          [touchTargetMedia]: {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.financeLayout.cardLayout.borderRadius,
          borderColor: theme.palette.finance.border.medium,
          backgroundColor: theme.palette.finance.surface.raised,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: ({ theme }) => ({
          borderRadius: radii.large,
          borderColor: theme.palette.finance.border.medium,
          backgroundColor: theme.palette.finance.surface.raised,
        }),
        elevation0: { backgroundImage: 'none' },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.financeLayout.cardLayout.padding.standard,
          '&:last-child': { paddingBottom: theme.financeLayout.cardLayout.padding.standard },
        }),
      },
    },
    MuiTextField: {
      defaultProps: { size: 'medium' },
    },
    MuiInputLabel: {
      // Finance forms use persistent floating labels. This avoids label/value
      // and label/placeholder collisions when edit values are populated after
      // mount, while keeping create/edit/view layouts visually stable.
      defaultProps: { shrink: true },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontWeight: 500,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }),
        // The shrunk (floating) label sits in the notched outline. Give it a
        // touch more letter-spacing and weight so it reads as a label, not body
        // text crammed against the border.
        shrink: {
          fontWeight: 600,
        },
        sizeSmall: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiOutlinedInput: {
      // Match the persistent floating-label contract above. Keeping the notch
      // open prevents the outline border from cutting through labels on empty
      // or asynchronously populated edit fields.
      defaultProps: { notched: true },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.financeLayout.cardLayout.borderRadius,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.finance.border.medium,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.finance.border.strong,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.finance.surface.sunken,
          },
          // Dense inputs (tables, inline filters, collection cells) stay compact.
          '&.MuiOutlinedInput-sizeSmall': {
            minHeight: 40,
            [touchTargetMedia]: { minHeight: 44 },
          },
          [touchTargetMedia]: {
            minHeight: 48,
          },
        }),
        input: {
          // Small inputs stay compact; medium inputs use MUI's default vertical
          // padding, which gives the text comfortable space and keeps the label
          // from feeling crammed against the border.
          '&.MuiInputBase-inputSizeSmall': {
            paddingBlock: '8.5px',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          marginTop: '6px',
          lineHeight: 1.4,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radii.medium },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radii.small },
        clickable: {
          [touchTargetMedia]: {
            minHeight: 44,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          [touchTargetMedia]: {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          [touchTargetMedia]: {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          [touchTargetMedia]: {
            minWidth: 52,
            minHeight: 44,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100dvh - 32px)',
          borderRadius: radii.large,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.financeLayout.dialog.padding,
          paddingBottom: theme.spacing(1),
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: theme.financeLayout.dialog.padding,
          paddingRight: theme.financeLayout.dialog.padding,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          gap: theme.financeLayout.dialog.gap,
          padding: theme.financeLayout.dialog.padding,
          paddingTop: theme.spacing(2),
          flexWrap: 'wrap',
          [theme.breakpoints.down('sm')]: {
            '& > .MuiButton-root': {
              flex: '1 1 auto',
            },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          [touchTargetMedia]: {
            minHeight: '44px',
            boxSizing: 'border-box',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: radii.small,
          marginInline: 4,
          [touchTargetMedia]: {
            minHeight: 44,
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.finance.border.medium}`,
          borderRadius: `${radii.medium}px !important`,
          backgroundColor: theme.palette.finance.surface.raised,
          overflow: 'hidden',
          '&::before': { display: 'none' },
          '& + &': { marginTop: theme.spacing(2) },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 48,
          paddingInline: theme.spacing(2),
          backgroundColor: theme.palette.finance.surface.subtle,
          '&.Mui-expanded': { minHeight: 48 },
          [touchTargetMedia]: { minHeight: 52 },
        }),
        content: {
          marginBlock: 12,
          '&.Mui-expanded': { marginBlock: 12 },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        enterDelay: 400,
        // Keep tooltip content beside its trigger so it remains inside the
        // trigger's banner/main/dialog landmark instead of being portaled as
        // unowned page content under <body>.
        slotProps: { popper: { disablePortal: true } },
      },
      styleOverrides: {
        tooltip: {
          maxWidth: 320,
          borderRadius: radii.small,
          fontSize: '0.75rem',
          lineHeight: 1.4,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          textUnderlineOffset: '0.15em',
          '&:focus-visible': {
            outline: focusOutline(theme.palette.primary.main),
            outlineOffset: focusRing.offset,
            borderRadius: radii.small,
          },
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.finance.surface.raised,
          border: `1px solid ${theme.palette.finance.border.medium}`,
          borderRadius: theme.financeLayout.cardLayout.borderRadius,
          overflow: 'auto',
          contain: 'inline-size',
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.finance.surface.sunken,
          '& .MuiTableCell-head': {
            borderBottom: `1px solid ${theme.palette.finance.border.medium}`,
          },
          [touchTargetMedia]: {
            '& .MuiTableCell-stickyHeader': {
              position: 'static',
            },
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: table.rowHeight,
          backgroundColor: theme.palette.background.paper,
          '&.MuiTableRow-hover:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: theme.palette.action.selected,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          height: table.rowHeight,
        },
        head: { fontWeight: 600, height: table.headerHeight },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.finance.border.subtle}`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: `${shell.compactHeaderHeight} !important`,
          [theme.breakpoints.up('sm')]: {
            minHeight: `${shell.headerHeight} !important`,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: shell.compactHeaderHeight,
        },
      },
    },
  };
}
